import { useCallback, useEffect, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { DashSidebar } from "@/components/DashSidebar";
import MessageList from "@/components/chat/MessageList";
import MessageInput from "@/components/chat/MessageInput";
import api from "@/lib/api";
import { streamChat } from "@/lib/chatStream";
import { toast } from "sonner";
import { goUpgrade } from "@/lib/planAccess";

const withKey = (m, idx) => ({ ...m, key: m.ts ? `${m.ts}-${m.role}` : `local-${idx}-${m.role}` });

export default function Coach() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const loadHistory = async () => {
      try {
        const { data } = await api.get("/chat/history");
        if (!cancelled) setMessages(data.map(withKey));
      } catch (err) {
        console.error("chat history load failed", err);
      }
    };
    loadHistory();
    api.get("/billing/usage").then(({data}) => setUsage(data)).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const send = useCallback(async () => {
    if (!input.trim() || streaming) return;
    const text = input;
    setInput("");
    const userMsg = { role: "user", content: text, key: `local-${Date.now()}-user` };
    const aiKey = `local-${Date.now()}-assistant`;
    setMessages((prev) => [...prev, userMsg, { role: "assistant", content: "", key: aiKey }]);
    setStreaming(true);
    let streamFailed = false;
    try {
      await streamChat({
        message: text,
        onDelta: (delta) => {
          setMessages((prev) => {
            const out = [...prev];
            const last = out[out.length - 1];
            out[out.length - 1] = { ...last, content: (last.content || "") + delta };
            return out;
          });
        },
        onError: (err) => {
          streamFailed = true;
          console.error("chat stream error", err);
          if (err.status === 403 || err.response?.status === 403 || /limit|upgrade/i.test(err.message || "")) {
            toast.error(err.message || "You have reached your AI Coach limit.");
            goUpgrade();
          } else {
            toast.error("Chat error: " + err.message);
          }
        },
      });
    } finally {
      if (streamFailed) {
        setMessages((prev) => prev.filter((m) => m.key !== aiKey));
      }
      setStreaming(false);
      api.get("/billing/usage").then(({data}) => setUsage(data)).catch(() => {});
    }
  }, [input, streaming]);

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="coach-page">
      <Navbar />
      <DashSidebar />
      <main className="lg:ml-60 pt-20 sm:pt-24 px-4 sm:px-6 md:px-10 pb-4 max-w-4xl mx-auto min-h-screen flex flex-col">
        <div className="mb-4 sm:mb-6 shrink-0">
          <div className="text-xs tracking-[0.2em] uppercase text-blue-500 font-semibold mb-2">// AI Coach · Career guidance</div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl tracking-tight font-medium">Career Coach Chatbot</h1>
          <p className="text-xs text-zinc-600 mt-2">{usage ? `${usage.used?.coach_messages || 0}/${usage.limits?.coach_messages || 0} messages this month` : ""}</p>
        </div>
        <div className="flex-1 overflow-y-auto space-y-4 pr-1 sm:pr-2" data-testid="chat-messages">
          <MessageList messages={messages} streaming={streaming} />
        </div>
        <div className="mt-4 shrink-0">
          <MessageInput value={input} onChange={setInput} onSend={send} disabled={streaming} />
        </div>
      </main>
    </div>
  );
}
